import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  deleteDeveloperMedia,
  developerMediaUrl,
  reorderDeveloperMedia,
  uploadDeveloperMedia,
  type DeveloperApp,
  type MediaAsset,
} from "./apps.api";
import { mediaQuery } from "./apps.queries";

const MAX_SCREENSHOTS = 8;

/**
 * Store media: the icon (extracted from the bundle, replaceable here) and
 * the screenshot gallery with upload, reorder, and remove.
 */
export function MediaCard({ app, enabled }: { app: DeveloperApp; enabled: boolean }) {
  const qc = useQueryClient();
  const media = useQuery(mediaQuery(app.packageName, enabled));
  const [error, setError] = useState<string | null>(null);
  const iconInput = useRef<HTMLInputElement>(null);
  const shotInput = useRef<HTMLInputElement>(null);

  const assets = media.data?.media ?? [];
  const icon = assets.find(asset => asset.id === app.iconAssetId) ?? assets.find(asset => asset.role === "store_icon") ?? null;
  const screenshots = assets
    .filter(asset => asset.role === "gallery_screenshot")
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["developer-media", app.packageName] }),
      qc.invalidateQueries({ queryKey: ["developer-apps"] }),
    ]);
  };
  const fail = (err: unknown) => setError(err instanceof Error ? err.message : "Request failed");

  const upload = useMutation({
    mutationFn: (input: { role: MediaAsset["role"]; file: File }) => uploadDeveloperMedia(app.packageName, input.role, input.file),
    onMutate: () => setError(null),
    onSuccess: refresh,
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (assetId: string) => deleteDeveloperMedia(app.packageName, assetId),
    onMutate: () => setError(null),
    onSuccess: refresh,
    onError: fail,
  });
  const reorder = useMutation({
    mutationFn: (order: string[]) => reorderDeveloperMedia(app.packageName, order),
    onMutate: () => setError(null),
    onSuccess: refresh,
    onError: fail,
  });

  const busy = upload.isPending || remove.isPending || reorder.isPending;
  const move = (index: number, delta: number) => {
    const order = screenshots.map(asset => asset.id);
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    [order[index], order[target]] = [order[target]!, order[index]!];
    reorder.mutate(order);
  };

  return (
    <Card className="mt-6 rounded-[16px] border-[#e0e4de] bg-white py-0 shadow-[0_1px_2px_rgba(20,21,27,0.06)] sm:rounded-[18px]">
      <CardHeader className="border-b border-[#eceeeb] px-4 py-4 sm:px-6 sm:py-5">
        <CardTitle className="font-display text-[18px]">Store media</CardTitle>
        <p className="text-sm text-[#747780]">
          The icon comes from <code className="rounded bg-[#f0f2ef] px-1 font-mono text-[12px]">icon.png</code> in your bundle; upload one here to override it. Screenshots are PNG or JPEG up to 2 MB, at most {MAX_SCREENSHOTS}. <code className="rounded bg-[#f0f2ef] px-1 font-mono text-[12px]">glassly publish</code> uploads a <code className="rounded bg-[#f0f2ef] px-1 font-mono text-[12px]">store/</code> folder the same way.
        </p>
      </CardHeader>
      <CardContent className="grid gap-6 p-4 sm:p-6">
        <div className="flex items-center gap-4">
          <div className="flex size-20 items-center justify-center overflow-hidden rounded-[18px] border border-[#e0e4de] bg-[#f7f8f6]">
            {icon ? (
              <img src={developerMediaUrl(app.packageName, icon.id)} alt="Store icon" className="size-full object-cover" />
            ) : (
              <ImagePlus className="size-6 text-[#a0a3aa]" />
            )}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold">Icon</div>
            <div className="text-xs text-[#747780]">
              {icon ? `${icon.width ?? "?"}×${icon.height ?? "?"} · ${icon.releaseId ? "from the bundle" : "uploaded override"}` : "No icon yet. Publish a release with icon.png or upload one."}
            </div>
            <div className="mt-2 flex gap-2">
              <Button variant="outline" size="sm" className="rounded-full" disabled={busy} onClick={() => iconInput.current?.click()}>
                {icon ? "Replace" : "Upload"} PNG
              </Button>
              {icon && !icon.releaseId ? (
                <Button variant="ghost" size="sm" className="rounded-full text-[#a64235]" disabled={busy} onClick={() => remove.mutate(icon.id)}>
                  Remove override
                </Button>
              ) : null}
            </div>
            <input
              ref={iconInput}
              type="file"
              accept="image/png"
              className="hidden"
              onChange={event => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) upload.mutate({ role: "store_icon", file });
              }}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold">Screenshots <span className="font-normal text-[#a0a3aa]">{screenshots.length}/{MAX_SCREENSHOTS}</span></div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              disabled={busy || screenshots.length >= MAX_SCREENSHOTS}
              onClick={() => shotInput.current?.click()}
            >
              <ImagePlus className="size-4" /> Add screenshot
            </Button>
            <input
              ref={shotInput}
              type="file"
              accept="image/png,image/jpeg"
              multiple
              className="hidden"
              onChange={async event => {
                const files = Array.from(event.target.files ?? []);
                event.target.value = "";
                for (const file of files.slice(0, MAX_SCREENSHOTS - screenshots.length)) {
                  await upload.mutateAsync({ role: "gallery_screenshot", file }).catch(() => undefined);
                }
              }}
            />
          </div>
          {media.isLoading ? (
            <div className="mt-3 text-sm text-[#747780]">Loading media…</div>
          ) : screenshots.length === 0 ? (
            <div className="mt-3 rounded-[12px] border border-dashed border-[#e0e4de] p-6 text-center text-sm text-[#747780]">
              No screenshots yet. Listings without screenshots hide the gallery on the phone.
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {screenshots.map((asset, index) => (
                <div key={asset.id} className="group relative overflow-hidden rounded-[12px] border border-[#e0e4de] bg-[#f7f8f6]">
                  <img src={developerMediaUrl(app.packageName, asset.id)} alt={asset.fileName} className="aspect-[9/16] w-full object-cover" />
                  <div className="flex items-center justify-between gap-1 border-t border-[#e0e4de] bg-white px-1.5 py-1">
                    <Button variant="ghost" size="icon-sm" disabled={busy || index === 0} onClick={() => move(index, -1)} aria-label="Move left">
                      <ArrowLeft className="size-4" />
                    </Button>
                    <span className="truncate text-[11px] text-[#747780]">{index + 1}</span>
                    <Button variant="ghost" size="icon-sm" disabled={busy || index === screenshots.length - 1} onClick={() => move(index, 1)} aria-label="Move right">
                      <ArrowRight className="size-4" />
                    </Button>
                    <Button variant="ghost" size="icon-sm" className="text-[#a64235]" disabled={busy} onClick={() => remove.mutate(asset.id)} aria-label="Remove screenshot">
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {busy ? <div className="flex items-center gap-2 text-sm text-[#747780]"><Loader2 className="size-4 animate-spin" /> Working…</div> : null}
        {error ? <p className="text-sm text-[#a64235]">{error}</p> : null}
      </CardContent>
    </Card>
  );
}
